/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

import { oc } from "@orpc/contract";
import * as z from "zod";
import { IdentifierTypeSchema } from "../common/identifier-type";
import { commonErrors } from "./base";

// =============================================================================
// PHONE STATUS TYPES
// =============================================================================

export const FrontendStatusFilterSchema = z.enum([
	"registered",
	"unregistered",
	"inactive",
	"active",
	"has_document",
	"no_document",
]);
export type FrontendStatusFilter = z.infer<typeof FrontendStatusFilterSchema>;

/**
 * Date range filter type - filter by messages or events
 */
export const DateRangeFilterTypeSchema = z.enum(["messages", "events"]);
export type DateRangeFilterType = z.infer<typeof DateRangeFilterTypeSchema>;

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

/**
 * Phone statistics status counts
 */
export const PhoneStatsStatusSchema = z.object({
	registered: z.number().int().min(0),
	unregistered: z.number().int().min(0),
	inactive: z.number().int().min(0),
	withDocument: z
		.number()
		.int()
		.min(0)
		.describe("Users with at least one document"),
});

/**
 * Phone statistics - country distribution and status counts
 */
export const PhoneStatsSchema = z.object({
	countries: z.record(z.string(), z.number().int().min(0)),
	status: PhoneStatsStatusSchema,
});
export type PhoneStats = z.infer<typeof PhoneStatsSchema>;

/**
 * Phone search result - a single contact result
 */
export const PhoneSearchResultSchema = z.object({
	user_id: z.string(),
	phone_e164: z.string(),
	conversation_id: z.string().nullish(),
	user_name: z.string().nullish(),
	/**
	 * Channel profile name (WhatsApp pushName etc.). Display-only hint for when
	 * `user_name` is missing — never a substitute for the authoritative name and
	 * never to be prefilled into scheduling or contact-edit inputs.
	 */
	nickname: z.string().nullish(),
	is_active: z.boolean(),
	is_favorite: z.boolean(),
	ai_enabled: z.boolean(),
	updated_at: z.string().nullish(),
	last_message_at: z.string().nullish(),
	last_event_at: z.string().nullish(),
	similarity: z.number().nullish().describe("Search similarity score (0-1)"),
	channel: IdentifierTypeSchema.nullish().describe(
		"Messaging channel (whatsapp, messenger, etc.)"
	),
	// Phoneless beneficiaries booked under this contact — the calendar drawer's
	// phone selector expands a contact into these, and a beneficiary-name search
	// surfaces the managing contact. Mirror of the server's phone.contract.
	beneficiaries: z
		.array(z.object({ id: z.string(), name: z.string().nullable() }))
		.nullish(),
});
export type PhoneSearchResult = z.infer<typeof PhoneSearchResultSchema>;

export const ContactSearchResultSchema = PhoneSearchResultSchema.extend({
	phone_e164: z.string().nullable(),
	address: z.string().nullable(),
});
export type ContactSearchResult = z.infer<typeof ContactSearchResultSchema>;

/**
 * Pagination info for offset/cursor contact listing
 */
export const PaginationInfoSchema = z.object({
	total: z.number().int().min(0),
	offset: z.number().int().min(0).nullish(),
	limit: z.number().int().positive().nullish(),
});
export type PaginationInfo = z.infer<typeof PaginationInfoSchema>;

/**
 * List all contacts response with pagination
 */
export const ListAllPayloadSchema = z.object({
	data: z.array(PhoneSearchResultSchema),
	pagination: PaginationInfoSchema,
	hasMore: z.boolean(),
	nextCursor: z.string().nullish(),
});
export type ListAllPayload = z.infer<typeof ListAllPayloadSchema>;

/**
 * Get recent contacts response - array of phone search results
 */
export const GetRecentPayloadSchema = z.array(PhoneSearchResultSchema);
export type GetRecentPayload = z.infer<typeof GetRecentPayloadSchema>;

// =============================================================================
// PHONE CONTRACT
// =============================================================================

export const phoneContract = {
	/**
	 * Get phone statistics - aggregated counts by country and status
	 */
	getStats: oc
		.route({
			method: "GET",
			path: "/api/phone/stats",
			summary: "Get phone statistics",
			description:
				"Get aggregated statistics about phone numbers in the system.",
			tags: ["Phone"],
		})
		.output(PhoneStatsSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			DATABASE_ERROR: {
				message: "Failed to retrieve statistics",
			},
		}),

	/**
	 * Search phone numbers with optional filters
	 */
	search: oc
		.route({
			method: "GET",
			path: "/api/phone/search",
			summary: "Search phone numbers",
			description:
				"Search phone numbers with optional filters for status, country, and date range.",
			tags: ["Phone"],
		})
		.input(
			z.object({
				q: z.string().max(500).optional().describe("Search query string"),
				limit: z.coerce
					.number()
					.int()
					.min(1)
					.max(1000)
					.optional()
					.default(100)
					.describe("Maximum number of results to return"),
				status: FrontendStatusFilterSchema.optional().describe(
					"Filter by phone status"
				),
				statuses: z
					.array(FrontendStatusFilterSchema)
					.max(6)
					.optional()
					.describe(
						"Filter by phone statuses (AND semantics; supersedes status)"
					),
				countries: z
					.array(z.string().max(10))
					.max(50)
					.optional()
					.describe("Filter by country codes (e.g., US, SA)"),
				date_range_type: DateRangeFilterTypeSchema.optional().describe(
					"Date range type for filtering"
				),
				date_from: z
					.string()
					.max(30)
					.optional()
					.describe("Start date (ISO string)"),
				date_to: z
					.string()
					.max(30)
					.optional()
					.describe("End date (ISO string)"),
			})
		)
		.output(z.array(ContactSearchResultSchema))
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			DATABASE_ERROR: {
				message: "Failed to search phones",
			},
		}),

	/**
	 * Get recent contacts ordered by last activity
	 */
	getRecent: oc
		.route({
			method: "GET",
			path: "/api/phone/recent",
			summary: "Get recent contacts",
			description: "Get recently active contacts, ordered by last activity.",
			tags: ["Phone"],
		})
		.input(
			z.object({
				limit: z.coerce
					.number()
					.int()
					.min(1)
					.max(1000)
					.optional()
					.default(50)
					.describe("Maximum number of contacts to return"),
			})
		)
		.output(z.array(PhoneSearchResultSchema))
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			DATABASE_ERROR: {
				message: "Failed to retrieve recent contacts",
			},
		}),

	/**
	 * List all contacts with pagination and optional filters
	 */
	listAll: oc
		.route({
			method: "GET",
			path: "/api/phone/all",
			summary: "List all contacts",
			description:
				"List contacts with offset/cursor pagination and optional filters.",
			tags: ["Phone"],
		})
		.input(
			z.object({
				// Offset-based pagination (for virtualized scrolling)
				offset: z.coerce
					.number()
					.int()
					.min(0)
					.optional()
					.describe("Offset for pagination (number of items to skip)"),
				// Cursor-based pagination
				limit: z.coerce
					.number()
					.int()
					.min(1)
					.max(1000)
					.optional()
					.describe("Maximum number of results"),
				startingAfter: z
					.string()
					.max(255)
					.optional()
					.describe("Cursor for forward pagination (user ID)"),
				endingBefore: z
					.string()
					.max(255)
					.optional()
					.describe("Cursor for backward pagination (user ID)"),
				// Filters
				status: FrontendStatusFilterSchema.optional().describe(
					"Filter by phone status"
				),
				statuses: z
					.array(FrontendStatusFilterSchema)
					.max(6)
					.optional()
					.describe(
						"Filter by phone statuses (AND semantics; supersedes status)"
					),
				countries: z
					.array(z.string().max(10))
					.max(50)
					.optional()
					.describe("Filter by country codes"),
				date_range_type: DateRangeFilterTypeSchema.optional().describe(
					"Date range type for filtering"
				),
				date_from: z
					.string()
					.max(30)
					.optional()
					.describe("Start date (ISO string)"),
				date_to: z
					.string()
					.max(30)
					.optional()
					.describe("End date (ISO string)"),
				exclude: z
					.string()
					.max(10_000)
					.optional()
					.describe("Comma-separated phone numbers to exclude"),
			})
		)
		.output(ListAllPayloadSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			DATABASE_ERROR: {
				message: "Failed to list contacts",
			},
		}),
};

export type PhoneContract = typeof phoneContract;

// =============================================================================
// TYPE EXPORTS
// =============================================================================

import type {
	InferContractRouterInputs,
	InferContractRouterOutputs,
} from "@orpc/contract";

// Contract input/output types using oRPC inference utilities
type PhoneInputs = InferContractRouterInputs<PhoneContract>;
type PhoneOutputs = InferContractRouterOutputs<PhoneContract>;

// Input types
export type GetStatsInput = PhoneInputs["getStats"];
export type SearchInput = PhoneInputs["search"];
export type GetRecentInput = PhoneInputs["getRecent"];
export type ListAllInput = PhoneInputs["listAll"];

// Output types
export type GetStatsOutput = PhoneOutputs["getStats"];
export type SearchOutput = PhoneOutputs["search"];
export type GetRecentOutput = PhoneOutputs["getRecent"];
export type ListAllOutput = PhoneOutputs["listAll"];
